/**
 * Route resolution, in order:
 *   1. fixed pages (/, /approvals, /profile, admin & reports screens by navigation code)
 *   2. entity routes (longest prefix): /route → list · /route/new → form · /route/:id → record · /route/:id/edit → form
 *   3. navigation pages: dashboards → module dashboard, custom pages → hub
 *   4. not found
 */
import { Component, type ReactNode } from 'react';
import { useRoute, Link } from './lib/router.tsx';
import { useSession } from './lib/session.tsx';
import { useMeta } from './lib/meta.tsx';
import { useI18n } from './lib/i18n.tsx';
import { Shell } from './components/Shell.tsx';
import { EmptyState, ErrorState, Spinner, Card } from './components/ui.tsx';
import { LoginPage } from './pages/LoginPage.tsx';
import { HomePage } from './pages/HomePage.tsx';
import { ModuleDashboardPage } from './pages/ModuleDashboardPage.tsx';
import { ListPage, FormPage } from './pages/EntityPages.tsx';
import { RecordPage } from './pages/RecordPage.tsx';
import { ApprovalsPage } from './pages/ApprovalsPage.tsx';
import { ReportsPage, KpiBoardPage } from './pages/ReportsPage.tsx';
import { HubPage } from './pages/HubPage.tsx';
import { ProfilePage } from './pages/ProfilePage.tsx';
import { UsersPage, SettingsPage, NumberingPage, AuditPage, OrganizationPage, SystemPage, DataPage } from './pages/admin/AdminPages.tsx';
import { WorkflowsPage } from './pages/admin/WorkflowsPage.tsx';
import { BrandingPage } from './pages/admin/BrandingPage.tsx';
import { IntegrationsPage } from './pages/admin/IntegrationsPage.tsx';
import { AuthorizationPage } from './pages/admin/AuthorizationPage.tsx';
import { ScanPage } from './pages/ScanPage.tsx';
import { RecycleBinPage } from './pages/RecycleBinPage.tsx';

const FIXED: Record<string, () => ReactNode> = {
  '/scan': () => <ScanPage />,
  '/approvals': () => <ApprovalsPage />,
  '/documents/approvals': () => <ApprovalsPage />,
  '/documents/recycle-bin': () => <RecycleBinPage />,
  '/profile': () => <ProfilePage />,
  '/reports': () => <ReportsPage />,
  '/reports/standard': () => <ReportsPage />,
  '/reports/library': () => <ReportsPage />,
  '/reports/kpis': () => <KpiBoardPage />,
  '/admin/users': () => <UsersPage />,
  '/admin/authorization': () => <AuthorizationPage />,
  '/admin/settings': () => <SettingsPage />,
  '/admin/branding': () => <BrandingPage />,
  '/admin/notifications': () => <SettingsPage />,
  '/admin/numbering': () => <NumberingPage />,
  '/admin/audit-log': () => <AuditPage />,
  '/admin/organization': () => <OrganizationPage />,
  '/admin/system': () => <SystemPage />,
  '/admin/workflows': () => <WorkflowsPage />,
  '/admin/integrations': () => <IntegrationsPage />,
  '/admin/data': () => <DataPage />,
};
/** screens every signed-in user has, whatever the matrix says */
const ALWAYS_OPEN = new Set(['/approvals', '/documents/approvals', '/profile', '/scan']);
/** entity routes that a fixed screen replaces (the list is still reachable through the screen) */
const FIXED_OVER_ENTITY = new Set(['/reports/library', '/reports/kpis', '/admin/organization', '/admin/integrations']);

class ErrorBoundary extends Component<{ children: ReactNode; resetKey: string }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidUpdate(prev: { resetKey: string }) { if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null }); }
  render() { return this.state.error ? <ErrorState error={this.state.error} retry={() => this.setState({ error: null })} /> : this.props.children; }
}

function Routes() {
  const { path } = useRoute();
  const meta = useMeta();
  const { t } = useI18n();
  if (!meta.ready) return meta.error ? <ErrorState error={meta.error} retry={() => window.location.reload()} /> : <Spinner />;
  if (path === '/') return <HomePage />;
  // the authorization matrix: a path whose screen the navigation (filtered by the server) does not offer is not opened
  if (!ALWAYS_OPEN.has(path) && !meta.page(path)) return <Card><EmptyState title={t('errors.noAccessTitle')} hint={t('errors.noAccessHint')} action={<Link to="/" className="btn primary">{t('nav.home')}</Link>} /></Card>;
  const fixed = FIXED[path];
  if (fixed) return <>{fixed()}</>;
  const hit = meta.resolve(path);
  if (hit && !(hit.rest.length === 0 && FIXED_OVER_ENTITY.has(path))) {
    const { entity, rest } = hit;
    if (rest.length === 0) return <ListPage def={entity} />;
    if (rest[0] === 'new') return <FormPage def={entity} />;
    if (rest.length === 1) return <RecordPage key={rest[0]} def={entity} id={rest[0]} />;
    if (rest[1] === 'edit') return <FormPage def={entity} id={rest[0]} />;
  }
  const page = meta.page(path);
  if (page) {
    if (page.page.kind === 'dashboard') return <ModuleDashboardPage module={page.module} />;
    if (page.page.entity && meta.entities.has(page.page.entity)) return <ListPage def={meta.entities.get(page.page.entity)!} />;
    return <HubPage module={page.module} page={page.page} />;
  }
  return <Card><EmptyState title={t('errors.notFound')} hint={path} action={<Link to="/" className="btn primary">{t('nav.home')}</Link>} /></Card>;
}

export function App() {
  const { session, loading } = useSession();
  const { path } = useRoute();
  if (loading) return <div className="empty" style={{ marginTop: '30vh' }}><Spinner /></div>;
  if (!session) return <LoginPage />;
  return <Shell><ErrorBoundary resetKey={path}><Routes /></ErrorBoundary></Shell>;
}
